import type { ComponentConfig } from "@puckeditor/core";
import { ImagePicker } from "../editor/ImagePicker";
import {
  BadgeBlock,
  ButtonBlock,
  DividerBlock,
  HeadingBlock,
  ImageBlock,
  GroupBlock,
  SectionBlock,
  SpacerBlock,
  TextBlock,
  type BadgeBlockProps,
  type ButtonBlockProps,
  type DividerBlockProps,
  type GroupBlockProps,
  type HeadingBlockProps,
  type ImageBlockProps,
  type SectionBlockProps,
  type SpacerBlockProps,
  type TextBlockProps,
} from "./primitives";
import {
  BadgeBlockFields,
  ButtonBlockFields,
  DividerBlockFields,
  GroupBlockFields,
  HeadingBlockFields,
  ImageBlockFields,
  SectionBlockFields,
  SpacerBlockFields,
  TextBlockFields,
} from "./primitives.fields";

export const HeadingBlockConfig: ComponentConfig<HeadingBlockProps> = {
  label: "Heading",
  fields: {
    ...HeadingBlockFields,
    text: { ...HeadingBlockFields.text, contentEditable: true },
  } as never,
  defaultProps: {
    text: "Your heading",
    level: "h2",
    size: "medium",
    color: "auto",
    align: "left",
    width: "normal",
    spacing: "normal",
  },
  render: ({ id, ...props }) => <HeadingBlock id={id} {...props} />,
};

export const TextBlockConfig: ComponentConfig<TextBlockProps> = {
  label: "Text",
  fields: {
    ...TextBlockFields,
    content: { ...TextBlockFields.content, contentEditable: true },
  } as never,
  defaultProps: {
    content: "Add your text here. Separate paragraphs with a blank line.",
    size: "medium",
    color: "auto",
    align: "left",
    width: "narrow",
    spacing: "normal",
  },
  render: ({ id, ...props }) => <TextBlock id={id} {...props} />,
};

export const ButtonBlockConfig: ComponentConfig<ButtonBlockProps> = {
  label: "Button",
  fields: {
    ...ButtonBlockFields,
    label: { ...ButtonBlockFields.label, contentEditable: true },
  } as never,
  defaultProps: {
    label: "Get a Quote",
    url: "#contact",
    variant: "primary",
    size: "medium",
    align: "left",
    spacing: "normal",
  },
  render: ({ id, ...props }) => <ButtonBlock id={id} {...props} />,
};

export const ImageBlockConfig: ComponentConfig<ImageBlockProps> = {
  label: "Image",
  fields: {
    ...ImageBlockFields,
    caption: { ...ImageBlockFields.caption, contentEditable: true },
    imageUrl: {
      ...ImageBlockFields.imageUrl,
      render: ({
        value,
        onChange,
      }: {
        value?: string;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Image" />,
    },
  } as never,
  defaultProps: {
    imageUrl: "",
    alt: "",
    caption: "",
    frame: "none",
    width: "normal",
    align: "center",
    spacing: "normal",
  },
  render: ({ id, ...props }) => <ImageBlock id={id} {...props} />,
};

export const SpacerBlockConfig: ComponentConfig<SpacerBlockProps> = {
  label: "Spacer",
  fields: SpacerBlockFields as never,
  defaultProps: { height: "medium" },
  render: ({ id, ...props }) => <SpacerBlock id={id} {...props} />,
};

export const DividerBlockConfig: ComponentConfig<DividerBlockProps> = {
  label: "Divider",
  fields: DividerBlockFields as never,
  defaultProps: { width: "normal", spacing: "normal" },
  render: ({ id, ...props }) => <DividerBlock id={id} {...props} />,
};

export const SectionBlockConfig: ComponentConfig<SectionBlockProps> = {
  label: "Section",
  fields: SectionBlockFields as never,
  defaultProps: {
    content: [],
    background: "default",
    padding: "normal",
    anchorId: "",
  } as never,
  render: ({ id, ...props }) => <SectionBlock id={id} {...props} />,
};

export const BadgeBlockConfig: ComponentConfig<BadgeBlockProps> = {
  label: "Badge",
  fields: BadgeBlockFields as never,
  defaultProps: {
    text: "Licensed & Insured",
    icon: "",
    tone: "accent",
    size: "medium",
    align: "left",
    spacing: "tight",
  },
  render: ({ id, ...props }) => <BadgeBlock id={id} {...props} />,
};

export const GroupBlockConfig: ComponentConfig<GroupBlockProps> = {
  label: "Group",
  fields: GroupBlockFields as never,
  defaultProps: {
    content: [],
    direction: "row",
    gap: "normal",
    wrap: "yes",
    align: "left",
    spacing: "tight",
  } as never,
  render: ({ id, ...props }) => <GroupBlock id={id} {...props} />,
};
